import { t } from 'i18next';
import _ from 'lodash';
import { EVERYONE_USERGROUP } from '@/administration/administration.constants';
import { openLinkBasedOnUserPreferences } from '@/core/utilities';
import { getDataLabProjectUrl } from '@/dataLab/dataLab.utilities';
import {
  IMPACT_REPORT_CONTEXT_CATEGORY,
  IMPACT_REPORT_NOMINATION_LABEL,
  OPEN_CREATE_MODAL_QUERY_PARAM,
  availableStages,
  stageIcons,
} from '@/impactReports/impactReport.types';
import { getKPIDisplayName, getKPIUnit } from '@/impactReports/impactReportDisplay.utilities';
import { getColumnDefinitions } from '@/impactReports/tiles/table.utilities';
import { getItemACL } from '@/utilities/acl.utilities';
import { errorToast, infoToast } from '@/utilities/toast.utilities';
import { sqContextApi } from '@seeqdev/api/endpoint/ContextApi';
import { sqItemsApi } from '@seeqdev/api/endpoint/ItemsApi';
import { sqMaterializedTablesApi } from '@seeqdev/api/endpoint/MaterializedTablesApi';
import { sqTableDefinitionsApi } from '@seeqdev/api/endpoint/TableDefinitionsApi';
import { sqUserGroupsApi } from '@seeqdev/api/endpoint/UserGroupsApi';
import { ColumnTypeEnum } from '@seeqdev/api/model/ColumnDefinitionBulkEditInputV1';
import { SeeqNames } from '@seeqdev/constants';
import type { ColDef } from 'ag-grid-community';
import type { SelectOption } from '@/formbuilder/SelectFormComponent';
import type { ImpactReportCategory, ImpactReportConfiguration, Stages } from '@/impactReports/impactReport.types';
import type { AceInputV1 } from '@seeqdev/api/model/AceInputV1';
import type { AclInputV1 } from '@seeqdev/api/model/AclInputV1';
import type { ColumnDefinitionInputV1 } from '@seeqdev/api/model/ColumnDefinitionInputV1';
import type { ColumnDefinitionOutputV1 } from '@seeqdev/api/model/ColumnDefinitionOutputV1';
import type { ScalarPropertyV1 } from '@seeqdev/api/model/ScalarPropertyV1';

export const doesStatusFilterIncludeReview = (statusFilter: unknown): boolean =>
  typeof statusFilter === 'object' &&
  statusFilter !== null &&
  'values' in statusFilter &&
  Array.isArray(statusFilter.values) &&
  statusFilter.values.length === 1 &&
  statusFilter.values[0] === 'review';

export function stageDisplayStringByIndex(stageIndex: number) {
  return t(`IMPACT_REPORT.${availableStages[stageIndex]?.toUpperCase()}`);
}

export function getImpactStageIcon(stage: Stages) {
  const index = availableStages.indexOf(stage);
  if (index > -1 && index <= stageIcons.length) {
    return stageIcons[availableStages.indexOf(stage)];
  } else {
    return stageIcons[0];
  }
}

export async function fetchCategories() {
  const { data } = await sqContextApi.findLabelCategories({});
  const categories = data?.categories?.filter(
    (category) => category.name?.startsWith('VCC_') && category.name !== IMPACT_REPORT_CONTEXT_CATEGORY,
  );
  if (!categories) return;
  const allOptions: ImpactReportCategory[] = [];
  await Promise.all(
    categories.map(async (category) => {
      const labelResponse = await sqContextApi.findLabels({ categoryId: category.id! });
      const labels = labelResponse.data.labels;
      const options = labels?.map((label) => ({ value: label.id!, label: label.name! }));
      allOptions.push({ category, options });
    }),
  );
  return allOptions;
}

export function getStageIndex(stage: Stages) {
  return availableStages.indexOf(stage.toLowerCase().trim() as Stages);
}

export function getAvailableAggregationSelectOptions(
  categories: ImpactReportCategory[] | undefined,
  showSite: boolean,
  showAssetPath: boolean,
  showLocation: boolean,
  timezone: { name: string },
): SelectOption<string | undefined>[] {
  const options = getColumnDefinitions(categories, showSite, showAssetPath, showLocation, timezone)
    .filter((column: ColDef<string, string>) => column.context?.canAggregateBy)
    .sort((a: ColDef<string, string>, b: ColDef<string, string>) => {
      if (a.headerName! < b.headerName!) return -1;
      if (a.headerName! > b.headerName!) return 1;
      return 0;
    })
    .map((columnDef) => ({
      label: columnDef.headerName!,
      value: columnDef.field!,
    }));

  return [{ label: t('IMPACT_REPORT.NONE'), value: 'none' }, ...options];
}

export async function addOrUpdateImpactReportConfiguration(tableId: string, configuration: ImpactReportConfiguration) {
  return sqTableDefinitionsApi.updateTableDefinition(
    { name: SeeqNames.ImpactReport.ImpactTableName, description: JSON.stringify(configuration) },
    { id: tableId },
  );
}

export async function addOrUpdateImpactReport(tableId: string, impactReport: ScalarPropertyV1[]) {
  return sqMaterializedTablesApi.publishEvents(
    {
      events: [
        {
          genericEvent: {
            properties: impactReport.map((prop) => ({
              name: prop.name,
              value: prop.value,
              unitOfMeasure: prop.unitOfMeasure,
            })),
          },
        },
      ],
    },
    { tableId },
  );
}

export function getAllSupportedBoxTypes() {
  return [
    {
      label: t('IMPACT_REPORT.TOTAL_USE_CASES'),
      value: 'useCaseCountBox',
      title: 'IMPACT_REPORT.TOTAL_USE_CASES',
      property: 'useCaseCount',
      id: 'useCaseCountBox',
    },
    {
      label: t('IMPACT_REPORT.TOTAL_IMPACT'),
      value: 'totalImpactBox',
      title: 'IMPACT_REPORT.TOTAL_IMPACT',
      property: 'overallImpact',
      id: 'totalImpactBox',
    },
    {
      label: t('IMPACT_REPORT.BIGGEST_SINGLE_IMPACT'),
      value: 'biggestImpactBox',
      title: 'IMPACT_REPORT.BIGGEST_SINGLE_IMPACT',
      property: 'biggestImpact',
      id: 'biggestImpactBox',
    },
  ];
}

export async function deleteCategory(category: ImpactReportCategory) {
  try {
    category.options &&
      (await Promise.all(
        category.options.map(async (option) => {
          try {
            await sqContextApi.deleteLabel({ labelId: option.value });
          } catch (e) {
            console.error(e);
            errorToast({ messageKey: 'Failed to delete associated label' });
          }
        }),
      ));
    await sqContextApi.deleteLabelCategory({ categoryId: category.category.id! });
  } catch (error) {
    console.error(error);
    errorToast({ messageKey: 'Failed to delete category' });
  }
}

export function goToWorksheet(worksheetId: string, preferNewTab: boolean) {
  openLinkBasedOnUserPreferences(`/view/${worksheetId}`, preferNewTab);
}

export function goToProject(projectId: string, preferNewTab: boolean) {
  openLinkBasedOnUserPreferences(getDataLabProjectUrl(projectId), preferNewTab);
}

export function goToWorksheetAndMakeImpactReport(worksheetId: string, workbookId: string, preferNewTab: boolean) {
  openLinkBasedOnUserPreferences(
    `workbook/${workbookId}/worksheet/${worksheetId}?${OPEN_CREATE_MODAL_QUERY_PARAM}`,
    preferNewTab,
  );
}

export async function getWorksheetLabels(worksheetId: string) {
  const { data } = await sqContextApi.getContextLabels({ ids: [{ itemId: worksheetId }] });
  return data.labels?.map((label) => label.contextId);
}

/**
 * This function returns the "nomination" label.
 * If it doesn't already exist and "create" is set to true it will create the category as well as the label and
 * return it.
 */
export async function getNominationLabel(doCreate = false) {
  const { data } = await sqContextApi.findLabelCategories({});
  const category = data?.categories?.find((category) => category.name === IMPACT_REPORT_CONTEXT_CATEGORY);
  let categoryId = category?.id;
  if (!category) {
    if (doCreate) {
      // create the category
      const categoryResponse = await sqContextApi.createLabelCategory({ name: IMPACT_REPORT_CONTEXT_CATEGORY });
      categoryId = categoryResponse.data?.id;
    } else {
      return;
    }
  }

  const labelResponse = await sqContextApi.findLabels({ categoryId: categoryId! });
  const labels = labelResponse.data.labels;
  const nominationLabel = labels?.find(({ name }) => name === IMPACT_REPORT_NOMINATION_LABEL);
  if (nominationLabel) return nominationLabel;

  if (!nominationLabel && doCreate) {
    const labelCreationResponse = await sqContextApi.createLabel({
      name: IMPACT_REPORT_NOMINATION_LABEL,
      categoryId: categoryId!,
    });
    return labelCreationResponse.data;
  }
  return null;
}

/**
 * To "nominate" a worksheet to be turned into an Impact Report we leverage Context labels.
 * Applying the "IMPACT_REPORT_NOMINATION_LABEL" to a worksheet will flag it and make it show up as an option in the
 * header menu.
 */
export async function nominateWorksheetToBecomeImpactReport(worksheetId: string, workbookId: string) {
  const nominationLabel = await getNominationLabel(true);
  if (!nominationLabel || !nominationLabel.id) return;
  try {
    await sqContextApi.createContextLabel(
      {
        datumId: workbookId,
        labelId: nominationLabel.id,
      },
      { itemId: worksheetId },
    );
    infoToast({ messageKey: 'IMPACT_REPORT.FLAGGED' });
    return true;
  } catch (e) {
    errorToast({ messageKey: 'IMPACT_REPORT.FLAG_ERROR' });
    return false;
  }
}

/**
 * Replaces "Journal Seeq Links" with builder links so the expected workstep/item/date range is displayed.
 */
export function replaceLinks(input: string, impactReportWorkbookId?: string, impactReportWorksheetId?: string): string {
  const replacements: { [key: string]: string } = {
    workstep: 'workbook/builder?workbookId=$1&worksheetId=$2&workstepId=$3&startFresh=false',
    condition: 'workbook/builder?trendItems=$1',
    signal: 'workbook/builder?trendItems=$1',
    scalar: 'workbook/builder?trendItems=$1',
    range:
      `workbook/builder?workbookId=${impactReportWorkbookId}&worksheetId=${impactReportWorksheetId}` +
      '&startFresh=false&displayStartTime=$1&displayEndTime=$2&investigateStartTime=$1&investigateEndTime=$2',
    capsule:
      'workbook/builder?trendItems=$1&displayStartTime=$2&displayEndTime=$3' +
      '&investigateStartTime=$2&investigateEndTime=$3',
  };

  // this regex matches all possible journal seeq links and replaces them with builder links:
  // it matches the type of link (workstep, condition, signal, scalar, range, capsule) as well as the relevant IDs
  // and uses the replacements object (defined above) to generate the builder link
  return input.replace(
    /href="\/links\?type=(workstep|condition|signal|scalar|range|capsule)&amp;(?:workbook=([a-fA-F0-9-]+)&amp;worksheet=([a-fA-F0-9-]+)&amp;workstep=([a-fA-F0-9-]+)|item=([a-fA-F0-9-]+)|start=([a-fA-F0-9-]+)&amp;end=([a-fA-F0-9-]+)|item=([a-fA-F0-9-]+)&amp;start=([a-fA-F0-9-]+)&amp;end=([a-fA-F0-9-]+))"/gi,
    (_, type, wb, ws, wsId, item1, start1, end1, item2, start2, end2) => {
      switch (type.toLowerCase()) {
        case 'workstep':
          return `href="${replacements[type.toLowerCase()].replace('$1', wb).replace('$2', ws).replace('$3', wsId)}"`;
        case 'range':
          return `href="${replacements[type.toLowerCase()].replaceAll('$1', start1).replaceAll('$2', end1)}"`;
        case 'capsule':
          return `href="${replacements[type.toLowerCase()]
            .replace('$1', item2)
            .replaceAll('$2', String(parseInt(start2, 10)))
            .replaceAll('$3', end2)}"`;
        default: // Handles 'condition', 'signal', and 'scalar'
          return `href="${replacements[type.toLowerCase()]?.replace('$1', item1)}"`;
      }
    },
  );
}

export async function removeNomination(labelId: string, itemId: string) {
  try {
    await sqContextApi.deleteContextLabel({ labelId, itemId });
    return true;
  } catch (e) {
    return false;
  }
}

/** Helper function to get column definition for a Materialized Table **/
export function getTableColumnDefinition(
  columnName: string,
  columnType: ColumnTypeEnum = ColumnTypeEnum.TEXT,
  isIndexed = false,
  isGenerated = false,
  description?: string,
): ColumnDefinitionInputV1 {
  return {
    columnName,
    columnRules: [
      {
        eventProperty: {
          propertyName: columnName,
        },
      },
    ],
    columnType,
    isGenerated,
    isHidden: false,
    isIndexed,
    sortAscending: true,
    sortIndex: 0,
    description,
  };
}

export async function verifyEveryoneGroupReadPermissions(id: string) {
  const { entries } = await getItemACL(id);
  const entryEveryone = _.filter(entries, (entry) => entry.identity?.name === EVERYONE_USERGROUP);
  return _.head(entryEveryone)?.permissions?.read || false;
}

export async function giveEveryoneGroupReadPermissions(id: string) {
  const { entries } = await getItemACL(id);
  const { data } = await sqUserGroupsApi.getUserGroups({ nameSearch: EVERYONE_USERGROUP });

  const everyoneGroup = _.find(data.items, { name: EVERYONE_USERGROUP });
  if (!everyoneGroup) {
    throw new Error(`User group ${EVERYONE_USERGROUP} not found`);
  }

  const inputEntries = entries?.map((entry) => ({
    identityId: entry.identity!.id!,
    permissions: {
      read: entry.permissions?.read ?? false,
      write: entry.permissions?.write ?? false,
      manage: entry.permissions?.manage ?? false,
    },
  }));

  const everyoneEntry: AceInputV1 = {
    identityId: everyoneGroup.id,
    permissions: {
      read: true,
      write: false,
      manage: false,
    },
  };

  const body: AclInputV1 = {
    entries: inputEntries ? [...inputEntries, everyoneEntry] : [everyoneEntry],
  };
  return sqItemsApi.setAcl(body, { id });
}

export async function removeEveryoneGroupReadPermissions(id: string) {
  const { entries } = await getItemACL(id);
  if (!entries || entries.length === 0) return;
  const everyoneEntry = _.find(entries, (entry) => entry.identity?.name === EVERYONE_USERGROUP);
  if (!everyoneEntry) return;

  const inputEntries = entries
    .filter((entry) => entry.identity?.name !== EVERYONE_USERGROUP)
    .map((entry) => ({
      identityId: entry.identity!.id!,
      permissions: {
        read: entry.permissions?.read ?? false,
        write: entry.permissions?.write ?? false,
        manage: entry.permissions?.manage ?? false,
      },
    }));

  const body: AclInputV1 = {
    entries: inputEntries,
  };
  return sqItemsApi.setAcl(body, { id });
}

export function getKPISelectOptions(kpiColumns: ColumnDefinitionOutputV1[]) {
  return kpiColumns.map((kpi) => ({
    label: `${getKPIDisplayName(kpi.columnName)} (${getKPIUnit(kpi.columnName)})`,
    value: kpi.columnName,
    title: `${getKPIDisplayName(kpi.columnName)}`,
    id: kpi.columnName,
    property: kpi.columnName,
  }));
}
